/* eslint-disable react-hooks/exhaustive-deps */
import { Delete, Edit } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid, styled, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { LoadingPage } from "../../../../common/LoadingPage";
import { BudgetType, CurrencyJSON } from "../../../../common/const";
import BudgetChart from "./BudgetChart";
import AdditionDeductionTable from "./AdditionDeductionTable";
import EditTotalBudget from "./EditTotalBudget";
import { useSelector } from "react-redux";

const DemoTypo = styled(Typography)(({ theme }) => ({
    color: 'gray',
    fontSize: 13
}));

const DemoTypo1 = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 13
}));

const Overview = ({
    projectId,
    budgetData,
    setBudgetOpen,
    budgetId,
    setBudgetId,
    getBudget,
    projectData,
    getData,
    budget,
    totalTax,
    tax,
    pointData
}) => {
    const { user, roles } = useSelector((state) => state.users);
    const isUser = roles.find((a) => a.id === user?.role_id);
    const [budgetType, setBudgetType] = useState('Current Total Project');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [budgetCompareType, setBudgetCompareType] = useState('Approved');
    const [addCompareType, setAddCompareType] = useState('Approved');
    const [totalBudgetOpen, setTotalBudgetOpen] = useState(false);
    const [individualBudget, setIndividualBudget] = useState(null);
    const [approvedAmount, setApproveAmount] = useState(null);
    const [actualAmount, setActualAmount] = useState(null);
    const [varianceAmount, setVarianceAmount] = useState(null);
    const [budgetArr, setBudgetArr] = useState([{
        total: {
            actual: totalTax + budget?.total_cost,
            approved: projectData?.total_approved_budget,
            variance: projectData?.total_approved_budget - (totalTax + budget?.total_cost),
        },
        unassigned: {
            actual: 0,
            approved: 0,
            variance: 0,
        }
    }]);
    const [pointArr, setPointArr] = useState([{
        total: {
            actual: totalTax + budget?.total_cost,
            approved: 0,
            variance: 0,
        }
    }]);
    const defaultPointTime = pointData?.find((a) => a.is_default);
    const [result, setResult] = useState(null);
    const [pointObj, setPointObj] = useState(null);

    const getAssignedBudget = async () => {
        try {
            setDataLoading(true);
            const response = await apiRequest('get', `/project/summary-by-budget/${projectId}` + (budgetType !== 'Unassigned' ? `/${budgetId}` : ''));
            if (response.data) {
                const { data } = response?.data;
                setDataLoading(false);
                setIndividualBudget(data);
                if (budgetType === 'Unassigned') {
                    setApproveAmount(Number(projectData?.total_approved_budget - budgetData.reduce((sum, item) => sum + Number(item.approved_amount), 0)))
                } else {
                    setApproveAmount(data?.approved_amount);
                }
                const fieldsToSum = [
                    "actual_amount",
                    "total_tax",
                    "total_freight",
                    "total_installation",
                    "total_fees",
                    "total_storage"
                ];

                let total = 0;
                for (const field of fieldsToSum) {
                    total += parseFloat(data[field]);
                }

                const fieldsToSub = [
                    "total_tradein",
                    "total_discounts"
                ];

                for (const field of fieldsToSub) {
                    total -= parseFloat(data[field]);
                }
                setActualAmount(total);
                if (budgetType === 'Unassigned') {
                    setVarianceAmount(Number(projectData?.total_approved_budget - budgetData.reduce((sum, item) => sum + Number(item.approved_amount), 0)) - total);
                } else {
                    setVarianceAmount(data?.approved_amount - total);
                }
            }
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (budgetType !== 'Current Total Project') {
            getAssignedBudget();
        } else {
            setApproveAmount(null);
            setActualAmount(null);
            setVarianceAmount(null);
        }
    }, [budgetType]);

    const { additions, deductions } = projectData;
    const calculateValue = (item, totalBudget) => {
        if (!item?.approved) return 0;
        const { type, value } = item.approved;
        if (type === 'rate') {
            return parseFloat((value * totalBudget / 100)).toFixed(2);
        }
        return parseFloat(value).toFixed(2);
    };

    const appObj = {
        equipment: projectData?.approved_equipment_cost || 0,
        discount: calculateValue(deductions?.discount, projectData?.total_approved_budget),
        reuse: calculateValue(deductions?.reuse, projectData?.total_approved_budget),
        tax: calculateValue(additions?.tax, projectData?.total_approved_budget),
        installation: calculateValue(additions?.installation, projectData?.total_approved_budget),
        storage: calculateValue(additions?.storage, projectData?.total_approved_budget),
        fees: calculateValue(additions?.fees, projectData?.total_approved_budget),
        freight: calculateValue(additions?.freight, projectData?.total_approved_budget),
        contingency: calculateValue(additions?.contingency, projectData?.total_approved_budget),
        inflation: calculateValue(additions?.inflation, projectData?.total_approved_budget)
    };

    const getValue = (item, budget) => {
        if (!item?.actual) return 0;

        const { scope, type, value, itemValue } = item.actual;
        const val = scope ? Number(scope === 'project' ? value : Number(itemValue)) : value;

        if (type === 'rate') {
            return ((Number(budget?.total_cost) || 0) * val) / 100;
        }

        return val;
    };

    const calculateDeduction = (item, budget) => {
        const value = getValue(item, budget);
        return value;
    }

    const calculateAddition = (item, budget) => {
        const baseValue = getValue(item, budget);
        return baseValue
    };

    const actualObj = {
        equipment: parseFloat(budget?.total_cost || 0).toFixed(2),
        discount: calculateDeduction(deductions?.discount, budget),
        reuse: calculateDeduction(deductions?.reuse, budget),
        installation: calculateAddition(additions?.installation, budget),
        contingency: calculateAddition(additions?.contingency, budget),
        inflation: calculateAddition(additions?.inflation, budget),
        storage: calculateAddition(additions?.storage, budget),
        fees: calculateAddition(additions?.fees, budget),
        freight: calculateAddition(additions?.freight, budget),
    }

    const calculateBudget = async () => {
        if (projectId) {
            const response = await apiRequest('get', `/project/summary-by-budget/${projectId}`);
            if (response.data) {
                const { data } = response?.data;
                const fieldsToSum = [
                    "actual_amount",
                    "total_tax",
                    "total_freight",
                    "total_installation",
                    "total_fees",
                    "total_storage"
                ];

                let total = 0;
                for (const field of fieldsToSum) {
                    total += parseFloat(data[field]);
                }

                const fieldsToSub = [
                    "total_tradein",
                    "total_discounts"
                ];

                for (const field of fieldsToSub) {
                    total -= parseFloat(data[field]);
                }
                setBudgetArr(prevData =>
                    prevData.map(item => ({
                        ...item,
                        unassigned: {
                            ...item.unassigned,
                            actual: total,
                            approved: Number(projectData?.total_approved_budget - budgetData.reduce((sum, item) => sum + Number(item.approved_amount), 0)),
                            variance: Number(projectData?.total_approved_budget - budgetData.reduce((sum, item) => sum + Number(item.approved_amount), 0)) - total
                        }
                    }))
                );
            }
        }
        if (budgetData.length > 0) {
            budgetData.forEach(async (a) => {
                const response = await apiRequest('get', `/project/summary-by-budget/${projectId}/${a.budget_id}`);
                if (response.data) {
                    const { data } = response?.data;
                    setDataLoading(false);
                    const fieldsToSum = [
                        "actual_amount",
                        "total_tax",
                        "total_freight",
                        "total_installation",
                        "total_fees",
                        "total_storage"
                    ];

                    let total = 0;
                    for (const field of fieldsToSum) {
                        total += parseFloat(data[field]);
                    }

                    const fieldsToSub = [
                        "total_tradein",
                        "total_discounts"
                    ];

                    for (const field of fieldsToSub) {
                        total -= parseFloat(data[field]);
                    }

                    const dynamicKey = a.budget_name.replace(/\s+/g, '_').toLowerCase(); // Convert to snake_case
                    setBudgetArr(prevData => {
                        return {
                            ...prevData, // Keep existing structure
                            "0": {
                                ...prevData["0"], // Keep existing total and unassigned
                                [dynamicKey]: { // Add new dynamic property
                                    actual: total,
                                    approved: data?.approved_amount,
                                    variance: data?.approved_amount - total
                                }
                            }
                        };
                    });
                }
            });
        }
    }

    useEffect(() => {
        calculateBudget();
    }, [budgetData]);


    const calculatePointTime = async (data, budgetId) => {
        const { item_data, project_data } = data;

        const projectTaxRate = project_data?.project_tax_rate ?? 0;
        const isTaxExempt = project_data?.is_tax_exempt ?? false;
        const taxableOption = project_data?.taxable_option
            ? JSON.parse(project_data.taxable_option)
            : [];

        if (budgetData.length > 0 && projectId) {
            budgetData.forEach(async (a) => {
                const response = await apiRequest('get', `/project/summary-by-budget/${projectId}/${a.budget_id}`);
                if (response.data) {
                    const { data } = response?.data;
                    setDataLoading(false);
                    const fieldsToSum = [
                        "actual_amount",
                        "total_tax",
                        "total_freight",
                        "total_installation",
                        "total_fees",
                        "total_storage"
                    ];

                    let total = 0;
                    for (const field of fieldsToSum) {
                        total += parseFloat(data[field]);
                    }

                    const fieldsToSub = [
                        "total_tradein",
                        "total_discounts"
                    ];

                    for (const field of fieldsToSub) {
                        total -= parseFloat(data[field]);
                    }

                    const dynamicKey = a.budget_name.replace(/\s+/g, '_').toLowerCase(); // Convert to snake_case
                    const filteredItems = item_data.filter((item) => item.budget_id === a.budget_id);

                    const result = filteredItems.reduce((acc, item) => {
                        const roomQty = item.room_quantity ?? 0;
                        const qty = item.quantity ?? 0;
                        const unitPrice = item.unit_price ?? 0;
                        const freight = Number(item.freight) || 0;
                        const storage = Number(item.storage) || 0;
                        const installation = Number(item.installation) || 0;
                        const fees = Number(item.fees) || 0;
                        const discounts = Number(item.discounts) || 0;
                        const tradein = Number(item.tradein) || 0;
                        const taxRate = Number(item.tax) || 0;

                        // subTotal = qty × unit_price
                        const subTotal = qty * unitPrice;

                        // unit price tax
                        const tax = !isTaxExempt
                            ? (subTotal * taxRate) / 100
                            : 0;

                        // additional tax on freight/installation/storage/fees
                        let additionalTax = 0;
                        if (taxableOption.length > 0) {
                            if (taxableOption.includes("Freight")) additionalTax += (freight * projectTaxRate) / 100;
                            if (taxableOption.includes("Installation")) additionalTax += (installation * projectTaxRate) / 100;
                            if (taxableOption.includes("Storage")) additionalTax += (storage * projectTaxRate) / 100;
                            if (taxableOption.includes("Fees")) additionalTax += (fees * projectTaxRate) / 100;
                        }

                        // finalTotal per item
                        const finalTotal = subTotal + tax + freight + installation + fees + storage - discounts - tradein + additionalTax;

                        // multiply by room_quantity only if unit_price exists
                        const finalTotalWithRoom = unitPrice ? finalTotal * roomQty : 0;

                        acc.total_sub_total += subTotal;
                        acc.total_tax += unitPrice ? tax * roomQty : 0;
                        acc.total_additional_tax += unitPrice ? additionalTax * roomQty : 0;
                        acc.total_freight += freight;
                        acc.total_storage += storage;
                        acc.total_installation += installation;
                        acc.total_fees += fees;
                        acc.total_discounts += discounts;
                        acc.total_tradein += tradein;
                        acc.grand_total += finalTotalWithRoom;

                        return acc;
                    }, {
                        total_sub_total: 0,
                        total_tax: 0,
                        total_additional_tax: 0,
                        total_freight: 0,
                        total_storage: 0,
                        total_installation: 0,
                        total_fees: 0,
                        total_discounts: 0,
                        total_tradein: 0,
                        grand_total: 0,
                    });

                    setPointArr(prevData => {
                        return {
                            ...prevData, // Keep existing structure
                            "0": {
                                ...prevData["0"], // Keep existing total and unassigned
                                [dynamicKey]: { // Add new dynamic property
                                    actual: total,
                                    approved: result.grand_total,
                                    variance: result.grand_total - total
                                }
                            }
                        };
                    });
                }
            });
        }

        if (budgetId) {
            // --- Filter by budgetId if provided
            const filteredItems = budgetId
                ? item_data.filter((item) => item.budget_id === budgetId)
                : item_data;

            const result = filteredItems.reduce((acc, item) => {
                const roomQty = item.room_quantity ?? 0;
                const qty = item.quantity ?? 0;
                const unitPrice = item.unit_price ?? 0;
                const freight = Number(item.freight) || 0;
                const storage = Number(item.storage) || 0;
                const installation = Number(item.installation) || 0;
                const fees = Number(item.fees) || 0;
                const discounts = Number(item.discounts) || 0;
                const tradein = Number(item.tradein) || 0;
                const taxRate = Number(item.tax) || 0;

                // subTotal = qty × unit_price
                const subTotal = qty * unitPrice;

                // unit price tax
                const tax = !isTaxExempt
                    ? (subTotal * taxRate) / 100
                    : 0;

                // additional tax on freight/installation/storage/fees
                let additionalTax = 0;
                if (taxableOption.length > 0) {
                    if (taxableOption.includes("Freight")) additionalTax += (freight * projectTaxRate) / 100;
                    if (taxableOption.includes("Installation")) additionalTax += (installation * projectTaxRate) / 100;
                    if (taxableOption.includes("Storage")) additionalTax += (storage * projectTaxRate) / 100;
                    if (taxableOption.includes("Fees")) additionalTax += (fees * projectTaxRate) / 100;
                }

                // finalTotal per item
                const finalTotal = subTotal + tax + freight + installation + fees + storage - discounts - tradein + additionalTax;

                // multiply by room_quantity only if unit_price exists
                const finalTotalWithRoom = unitPrice ? finalTotal * roomQty : 0;

                acc.total_sub_total += subTotal;
                acc.total_tax += unitPrice ? tax * roomQty : 0;
                acc.total_additional_tax += unitPrice ? additionalTax * roomQty : 0;
                acc.total_freight += freight;
                acc.total_storage += storage;
                acc.total_installation += installation;
                acc.total_fees += fees;
                acc.total_discounts += discounts;
                acc.total_tradein += tradein;
                acc.grand_total += finalTotalWithRoom;

                return acc;
            }, {
                total_sub_total: 0,
                total_tax: 0,
                total_additional_tax: 0,
                total_freight: 0,
                total_storage: 0,
                total_installation: 0,
                total_fees: 0,
                total_discounts: 0,
                total_tradein: 0,
                grand_total: 0,
            });
            setResult(result.grand_total);
        } else {
            const result = item_data.reduce((acc, item) => {
                const roomQty = item.room_quantity ?? 0;
                const qty = item.quantity ?? 0;
                const unitPrice = item.unit_price ?? 0;
                const taxRate = Number(item.tax) || 0;
                const freight = Number(item.freight) || 0;
                const storage = Number(item.storage) || 0;
                const installation = Number(item.installation) || 0;
                const fees = Number(item.fees) || 0;

                // subTotal = qty × unit_price
                const totalCost = qty * unitPrice * roomQty;

                // unit price tax
                const tax = !isTaxExempt
                    ? (totalCost * taxRate) / 100
                    : 0;

                let additionalTax = 0;
                if (taxableOption.length > 0) {
                    if (taxableOption.includes("Freight")) additionalTax += (freight * projectTaxRate) / 100;
                    if (taxableOption.includes("Installation")) additionalTax += (installation * projectTaxRate) / 100;
                    if (taxableOption.includes("Storage")) additionalTax += (storage * projectTaxRate) / 100;
                    if (taxableOption.includes("Fees")) additionalTax += (fees * projectTaxRate) / 100;
                }

                // multiply by room_quantity only if unit_price exists
                const finalTotalWithRoom = totalCost + tax;

                acc.total_sub_total += totalCost;
                acc.total_tax += unitPrice ? tax + (additionalTax * roomQty) : 0;
                acc.grand_total += finalTotalWithRoom;
                return acc;
            }, {
                total_sub_total: 0,
                total_tax: 0,
                total_additional_tax: 0,
                grand_total: 0,
            });

            const actualObj = {
                equipment: parseFloat(result.total_sub_total || 0).toFixed(2),
                discount: calculateDeduction(project_data?.deductions?.discount, { total_cost: result?.total_sub_total }),
                reuse: calculateDeduction(project_data?.deductions?.reuse, { total_cost: result?.total_sub_total }),
                installation: calculateAddition(project_data?.additions?.installation, { total_cost: result?.total_sub_total }),
                contingency: calculateAddition(project_data?.additions?.contingency, { total_cost: result?.total_sub_total }),
                inflation: calculateAddition(project_data?.additions?.inflation, { total_cost: result?.total_sub_total }),
                storage: calculateAddition(project_data?.additions?.storage, { total_cost: result?.total_sub_total }),
                fees: calculateAddition(project_data?.additions?.fees, { total_cost: result?.total_sub_total }),
                freight: calculateAddition(project_data?.additions?.freight, { total_cost: result?.total_sub_total }),
            }
            let averageTax = 0;
            if (taxableOption.length > 0) {
                if (taxableOption.includes("Freight")) averageTax += (actualObj.freight * projectTaxRate / 100);
                if (taxableOption.includes("Installation")) averageTax += (actualObj.installation * projectTaxRate / 100);
                if (taxableOption.includes("Storage")) averageTax += (actualObj.storage * projectTaxRate / 100);
                if (taxableOption.includes("Fees")) averageTax += (actualObj.fees * projectTaxRate / 100);
            }
            actualObj.total_tax = result?.total_tax + averageTax;
            setPointObj(actualObj);
            setResult(Number(actualObj.equipment) + result?.total_tax + averageTax + actualObj.installation + actualObj.inflation + actualObj.storage + actualObj.contingency + actualObj.fees + actualObj.freight - actualObj.reuse - actualObj.discount);
        }
    };

    useEffect(() => {
        if (defaultPointTime?.item_data?.length > 0) {
            calculatePointTime(defaultPointTime, budgetId);
        }
    }, [defaultPointTime?.item_data, budgetId, budgetType, projectId]);

    useEffect(() => {
        if (budgetType === 'Current Total Project' && pointArr?.length > 0) {
            setPointArr(prevData =>
                prevData.map(item => ({
                    ...item,
                    total: {
                        ...item.total,
                        approved: result,
                        variance: parseFloat(Number(result) - Number(item.total.actual)).toFixed(1)
                    }
                }))
            );
        } else {
            setPointArr([]);
        }
    }, [result, budgetType]);

    return (
        <>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            {totalBudgetOpen && (
                <EditTotalBudget
                    projectId={projectId}
                    totalBudgetOpen={totalBudgetOpen}
                    setTotalBudgetOpen={setTotalBudgetOpen}
                    projectData={projectData}
                    getData={getData}
                    budget={budget}
                />
            )}
            <Dialog open={deleteOpen} fullWidth maxWidth="xs">
                <DialogContent>
                    {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
                    Are you sure you want to delete this one?
                </DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => {
                            setDeleteOpen(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                setDataLoading(true);
                                let response = await apiRequest('delete', `/budget/${budgetId}`);
                                if (response?.data) {
                                    setDataLoading(false);
                                    showNotification(response?.data?.message, 'success');
                                    getBudget();
                                    setBudgetType('Current Total Project');
                                    setDeleteOpen(false);
                                }
                            } catch (error) {
                                setDataLoading(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid container spacing={1} mt={0.2}>
                <Grid item xs={12} md={10}>
                    <Button
                        variant={budgetType === 'Current Total Project' ? 'contained' : 'outlined'}
                        size="Medium"
                        style={{
                            borderRadius: 20,
                            fontSize: '12px',
                            textTransform: 'capitalize'
                        }}
                        onClick={() => {
                            setBudgetId(null);
                            setBudgetType('Current Total Project');
                        }}
                    >
                        Current Total Project
                    </Button>
                    <Button
                        variant={budgetType === 'Unassigned' ? 'contained' : 'outlined'}
                        size="Medium"
                        style={{
                            marginLeft: 5,
                            borderRadius: 20,
                            fontSize: '12px',
                            textTransform: 'capitalize'
                        }}
                        onClick={() => {
                            setBudgetId(null);
                            setBudgetType('Unassigned');
                        }}
                    >
                        Unassigned
                    </Button>
                    {budgetData?.length > 0 && budgetData.map((a) => (
                        <Button
                            variant={budgetType === a?.budget_name ? 'contained' : 'outlined'}
                            size="Medium"
                            style={{
                                marginLeft: 5,
                                borderRadius: 20,
                                fontSize: '12px',
                                textTransform: 'capitalize'
                            }}
                            onClick={() => {
                                setBudgetType(a?.budget_name);
                                setBudgetId(a.budget_id);
                            }}
                        >
                            {a?.budget_name}
                        </Button>
                    ))}
                    {isUser?.code !== 'orgGuestUser' && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{budgetType}</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {budgetType !== 'Unassigned' && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<Edit />}
                                        sx={{
                                            textTransform: 'capitalize'
                                        }}
                                        onClick={() => {
                                            if (budgetType !== 'Current Total Project') {
                                                setBudgetOpen(true);
                                            } else {
                                                setTotalBudgetOpen(true);
                                            }
                                        }}
                                    >
                                        Edit Budget
                                    </Button>
                                )}
                                {(budgetType !== 'Current Total Project' && budgetType !== 'Unassigned') && (
                                    <Tooltip title="Delete Budget">
                                        <Delete color="secondary" sx={{ cursor: 'pointer' }} onClick={() => setDeleteOpen(true)} />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    )}
                    {budgetType === 'Current Total Project' &&
                        (<>
                            <Box sx={{ border: '1px solid lightgray', padding: '20px 10px 10px 10px' }} mt={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography style={{ fontWeight: 'bold' }}>Compare Budget By</Typography>
                                    <Button
                                        variant={budgetCompareType === 'Point In Time' ? 'contained' : 'outlined'}
                                        size="Medium"
                                        style={{
                                            fontSize: '12px',
                                            textTransform: 'capitalize'
                                        }}
                                        onClick={() => {
                                            setBudgetCompareType('Point In Time')
                                        }}
                                    >
                                        Point in Time
                                    </Button>
                                    <Button
                                        variant={budgetCompareType === 'Approved' ? 'contained' : 'outlined'}
                                        size="Medium"
                                        style={{
                                            fontSize: '12px',
                                            textTransform: 'capitalize'
                                        }}
                                        onClick={() => {
                                            setBudgetCompareType('Approved')
                                        }}
                                    >
                                        Approved
                                    </Button>
                                </Box>
                                <Box mt={1}>
                                    {budgetCompareType === 'Approved' ? (
                                        <BudgetChart
                                            height={350}
                                            series={[{
                                                name: 'Approved',
                                                data: [
                                                    budgetArr[0]?.total?.approved || 0,
                                                    budgetArr[0]?.unassigned?.approved || 0,
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        return budgetArr[0]?.[key]?.approved || 0;
                                                    })
                                                ]
                                            }, {
                                                name: 'Actual',
                                                data: [
                                                    budgetArr[0]?.total?.actual || 0,
                                                    budgetArr[0]?.unassigned?.actual || 0,
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        return budgetArr[0]?.[key]?.actual || 0;
                                                    })
                                                ]
                                            }, {
                                                name: 'Variance Under Budget',
                                                data: [
                                                    Math.max(0, (budgetArr[0]?.total?.approved || 0) - (budgetArr[0]?.total?.actual || 0)),
                                                    Math.max(0, (budgetArr[0]?.unassigned?.approved || 0) - (budgetArr[0]?.unassigned?.actual || 0)),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        const approved = parseInt(budgetArr[0]?.[key]?.approved) || 0;
                                                        const actual = budgetArr[0]?.[key]?.actual || 0;
                                                        return Math.max(0, approved - actual);
                                                    })
                                                ]
                                            }, {
                                                name: 'Variance Over Budget',
                                                data: [
                                                    Math.min(0, (budgetArr[0]?.total?.approved || 0) - (budgetArr[0]?.total?.actual || 0)),
                                                    Math.min(0, (budgetArr[0]?.unassigned?.approved || 0) - (budgetArr[0]?.unassigned?.actual || 0)),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        const approved = parseInt(budgetArr[0]?.[key]?.approved) || 0;
                                                        const actual = budgetArr[0]?.[key]?.actual || 0;
                                                        return Math.min(0, approved - actual);
                                                    })
                                                ]
                                            }]}
                                            categories={[
                                                'Total',
                                                'Unassigned',
                                                ...budgetData.map((a) => a.budget_name)
                                            ]}
                                            title={`${CurrencyJSON.find((a) => a.value === projectData.currency)?.symbol} (millions)`}
                                        />
                                    ) : (
                                        <BudgetChart
                                            height={350}
                                            series={[{
                                                name: 'Point In Time',
                                                data: [
                                                    pointArr[0]?.total?.approved || 0,
                                                    // Unassigned = Total - Sum of all budgets
                                                    (pointArr[0]?.total?.approved || 0) - budgetData.reduce((sum, a) => {
                                                        const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                        return sum + (pointArr[0]?.[key]?.approved || 0);
                                                    }, 0),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        return pointArr[0]?.[key]?.approved || 0;
                                                    })
                                                ]
                                            }, {
                                                name: 'Actual',
                                                data: [
                                                    pointArr[0]?.total?.actual || 0,
                                                    // Unassigned = Total Actual - Sum of all budgets actual
                                                    (pointArr[0]?.total?.actual || 0) - budgetData.reduce((sum, a) => {
                                                        const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                        return sum + (pointArr[0]?.[key]?.actual || 0);
                                                    }, 0),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        return pointArr[0]?.[key]?.actual || 0;
                                                    })
                                                ]
                                            }, {
                                                name: 'Variance Under Budget',
                                                data: [
                                                    Math.max(0, (pointArr[0]?.total?.approved || 0) - (pointArr[0]?.total?.actual || 0)),
                                                    // Unassigned variance under budget
                                                    (() => {
                                                        const totalApproved = pointArr[0]?.total?.approved || 0;
                                                        const totalActual = pointArr[0]?.total?.actual || 0;
                                                        const budgetsApproved = budgetData.reduce((sum, a) => {
                                                            const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                            return sum + (pointArr[0]?.[key]?.approved || 0);
                                                        }, 0);
                                                        const budgetsActual = budgetData.reduce((sum, a) => {
                                                            const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                            return sum + (pointArr[0]?.[key]?.actual || 0);
                                                        }, 0);
                                                        const unassignedApproved = totalApproved - budgetsApproved;
                                                        const unassignedActual = totalActual - budgetsActual;
                                                        return Math.max(0, unassignedApproved - unassignedActual);
                                                    })(),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        const approved = parseInt(pointArr[0]?.[key]?.approved) || 0;
                                                        const actual = pointArr[0]?.[key]?.actual || 0;
                                                        return Math.max(0, approved - actual);
                                                    })
                                                ]
                                            }, {
                                                name: 'Variance Over Budget',
                                                data: [
                                                    Math.min(0, (pointArr[0]?.total?.approved || 0) - (pointArr[0]?.total?.actual || 0)),
                                                    // Unassigned variance over budget
                                                    (() => {
                                                        const totalApproved = pointArr[0]?.total?.approved || 0;
                                                        const totalActual = pointArr[0]?.total?.actual || 0;
                                                        const budgetsApproved = budgetData.reduce((sum, a) => {
                                                            const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                            return sum + (pointArr[0]?.[key]?.approved || 0);
                                                        }, 0);
                                                        const budgetsActual = budgetData.reduce((sum, a) => {
                                                            const key = a.budget_name.toLowerCase().replace(/\s+/g, '_');
                                                            return sum + (pointArr[0]?.[key]?.actual || 0);
                                                        }, 0);
                                                        const unassignedApproved = totalApproved - budgetsApproved;
                                                        const unassignedActual = totalActual - budgetsActual;
                                                        return Math.min(0, unassignedApproved - unassignedActual);
                                                    })(),
                                                    ...budgetData.map((a) => a.budget_name).map(name => {
                                                        const key = name.toLowerCase().replace(/\s+/g, '_');
                                                        const approved = parseInt(pointArr[0]?.[key]?.approved) || 0;
                                                        const actual = pointArr[0]?.[key]?.actual || 0;
                                                        return Math.min(0, approved - actual);
                                                    })
                                                ]
                                            }]}
                                            categories={[
                                                'Total',
                                                'Unassigned',
                                                ...budgetData.map((a) => a.budget_name)
                                            ]}
                                            title={`${CurrencyJSON.find((a) => a.value === projectData.currency)?.symbol} (millions)`}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ border: '1px solid lightgray', padding: '20px 10px 10px 10px' }} mt={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography style={{ fontWeight: 'bold' }}>Compare Additions and Deductions By</Typography>
                                    <Button
                                        variant={addCompareType === 'Point In Time' ? 'contained' : 'outlined'}
                                        size="Medium"
                                        style={{
                                            fontSize: '12px',
                                            textTransform: 'capitalize'
                                        }}
                                        onClick={() => {
                                            setAddCompareType('Point In Time')
                                        }}
                                    >
                                        Point in Time
                                    </Button>
                                    <Button
                                        variant={addCompareType === 'Approved' ? 'contained' : 'outlined'}
                                        size="Medium"
                                        style={{
                                            fontSize: '12px',
                                            textTransform: 'capitalize'
                                        }}
                                        onClick={() => {
                                            setAddCompareType('Approved')
                                        }}
                                    >
                                        Approved
                                    </Button>
                                </Box>
                                <Box mt={1}>
                                    {addCompareType === 'Approved' ? (
                                        <BudgetChart
                                            height={350}
                                            series={[{
                                                name: 'Approved',
                                                data: [
                                                    appObj?.equipment,
                                                    appObj?.discount,
                                                    appObj?.reuse,
                                                    appObj?.tax,
                                                    appObj?.installation,
                                                    appObj?.storage,
                                                    appObj?.freight,
                                                    appObj?.fees,
                                                    appObj?.contingency,
                                                    appObj?.inflation,
                                                ]
                                            }, {
                                                name: 'Actual',
                                                data: [
                                                    actualObj?.equipment,
                                                    actualObj?.discount,
                                                    actualObj?.reuse,
                                                    tax,
                                                    actualObj?.installation,
                                                    actualObj?.storage,
                                                    actualObj?.freight,
                                                    actualObj?.fees,
                                                    actualObj?.contingency,
                                                    actualObj?.inflation,
                                                ]
                                            }, {
                                                name: 'Variance Under Budget',
                                                data: [
                                                    appObj?.equipment - actualObj?.equipment > 0 ? appObj?.equipment - actualObj?.equipment : 0,
                                                    appObj?.discount - actualObj?.discount > 0 ? appObj?.discount - actualObj?.discount : 0,
                                                    appObj?.reuse - actualObj?.reuse > 0 ? appObj?.reuse - actualObj?.reuse : 0,
                                                    appObj?.tax - tax > 0 ? appObj?.tax - tax : 0,
                                                    appObj?.installation - actualObj?.installation > 0 ? appObj?.installation - actualObj?.installation : 0,
                                                    appObj?.storage - actualObj?.storage > 0 ? appObj?.storage - actualObj?.storage : 0,
                                                    appObj?.freight - actualObj?.freight > 0 ? appObj?.freight - actualObj?.freight : 0,
                                                    appObj?.fees - actualObj?.fees > 0 ? appObj?.fees - actualObj?.fees : 0,
                                                    appObj?.contingency - actualObj?.contingency > 0 ? appObj?.contingency - actualObj?.contingency : 0,
                                                    appObj?.inflation - actualObj?.inflation > 0 ? appObj?.inflation - actualObj?.inflation : 0
                                                ]
                                            }, {
                                                name: 'Variance Over Budget',
                                                data: [
                                                    appObj?.equipment - actualObj?.equipment < 0 ? appObj?.equipment - actualObj?.equipment : 0,
                                                    appObj?.discount - actualObj?.discount < 0 ? appObj?.discount - actualObj?.discount : 0,
                                                    appObj?.reuse - actualObj?.reuse < 0 ? appObj?.reuse - actualObj?.reuse : 0,
                                                    appObj?.tax - tax < 0 ? appObj?.tax - tax : 0,
                                                    appObj?.installation - actualObj?.installation < 0 ? appObj?.installation - actualObj?.installation : 0,
                                                    appObj?.storage - actualObj?.storage < 0 ? appObj?.storage - actualObj?.storage : 0,
                                                    appObj?.freight - actualObj?.freight < 0 ? appObj?.freight - actualObj?.freight : 0,
                                                    appObj?.fees - actualObj?.fees < 0 ? appObj?.fees - actualObj?.fees : 0,
                                                    appObj?.contingency - actualObj?.contingency < 0 ? appObj?.contingency - actualObj?.contingency : 0,
                                                    appObj?.inflation - actualObj?.inflation < 0 ? appObj?.inflation - actualObj?.inflation : 0
                                                ]
                                            }]}
                                            categories={['Equipment', 'Discounts', 'Trade In', 'Tax', 'Installation', 'Storage', 'Freight', 'Fees', 'Contingency', 'Inflation']}
                                            title={`${CurrencyJSON.find((a) => a.value === projectData.currency)?.symbol} (millions)`}
                                        />
                                    ) : (
                                        <BudgetChart
                                            height={350}
                                            series={[{
                                                name: 'Point In Time',
                                                data: [
                                                    pointObj?.equipment,
                                                    pointObj?.discount,
                                                    pointObj?.reuse,
                                                    pointObj?.total_tax,
                                                    pointObj?.installation,
                                                    pointObj?.storage,
                                                    pointObj?.freight,
                                                    pointObj?.fees,
                                                    pointObj?.contingency,
                                                    pointObj?.inflation,
                                                ]
                                            }, {
                                                name: 'Actual',
                                                data: [
                                                    actualObj?.equipment,
                                                    actualObj?.discount,
                                                    actualObj?.reuse,
                                                    tax,
                                                    actualObj?.installation,
                                                    actualObj?.storage,
                                                    actualObj?.freight,
                                                    actualObj?.fees,
                                                    actualObj?.contingency,
                                                    actualObj?.inflation,
                                                ]
                                            }, {
                                                name: 'Variance Under Budget',
                                                data: [
                                                    pointObj?.equipment - actualObj?.equipment > 0 ? pointObj?.equipment - actualObj?.equipment : 0,
                                                    pointObj?.discount - actualObj?.discount > 0 ? pointObj?.discount - actualObj?.discount : 0,
                                                    pointObj?.reuse - actualObj?.reuse > 0 ? pointObj?.reuse - actualObj?.reuse : 0,
                                                    pointObj?.total_tax - tax > 0 ? pointObj?.total_tax - tax : 0,
                                                    pointObj?.installation - actualObj?.installation > 0 ? pointObj?.installation - actualObj?.installation : 0,
                                                    pointObj?.storage - actualObj?.storage > 0 ? pointObj?.storage - actualObj?.storage : 0,
                                                    pointObj?.freight - actualObj?.freight > 0 ? pointObj?.freight - actualObj?.freight : 0,
                                                    pointObj?.fees - actualObj?.fees > 0 ? pointObj?.fees - actualObj?.fees : 0,
                                                    pointObj?.contingency - actualObj?.contingency > 0 ? pointObj?.contingency - actualObj?.contingency : 0,
                                                    pointObj?.inflation - actualObj?.inflation > 0 ? pointObj?.inflation - actualObj?.inflation : 0
                                                ]
                                            }, {
                                                name: 'Variance Over Budget',
                                                data: [
                                                    pointObj?.equipment - actualObj?.equipment < 0 ? pointObj?.equipment - actualObj?.equipment : 0,
                                                    pointObj?.discount - actualObj?.discount < 0 ? pointObj?.discount - actualObj?.discount : 0,
                                                    pointObj?.reuse - actualObj?.reuse < 0 ? pointObj?.reuse - actualObj?.reuse : 0,
                                                    pointObj?.total_tax - tax < 0 ? pointObj?.total_tax - tax : 0,
                                                    pointObj?.installation - actualObj?.installation < 0 ? pointObj?.installation - actualObj?.installation : 0,
                                                    pointObj?.storage - actualObj?.storage < 0 ? pointObj?.storage - actualObj?.storage : 0,
                                                    pointObj?.freight - actualObj?.freight < 0 ? pointObj?.freight - actualObj?.freight : 0,
                                                    pointObj?.fees - actualObj?.fees < 0 ? pointObj?.fees - actualObj?.fees : 0,
                                                    pointObj?.contingency - actualObj?.contingency < 0 ? pointObj?.contingency - actualObj?.contingency : 0,
                                                    pointObj?.inflation - actualObj?.inflation < 0 ? pointObj?.inflation - actualObj?.inflation : 0
                                                ]
                                            }]}
                                            categories={['Equipment', 'Discounts', 'Trade In', 'Tax', 'Installation', 'Storage', 'Freight', 'Fees', 'Contingency', 'Inflation']}
                                            title={`${CurrencyJSON.find((a) => a.value === projectData.currency)?.symbol} (millions)`}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </>)}
                    {budgetType === 'Current Total Project' && (
                        <Box sx={{ border: '1px solid lightgray', padding: '20px 10px 10px 10px' }} mt={1}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography style={{ fontWeight: 'bold' }}>Additions and Deductions</Typography>
                            </Box>
                            <AdditionDeductionTable
                                projectData={projectData}
                                budget={budget}
                                tax={tax}
                                totalTax={totalTax}
                            />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={2}>
                    <Box sx={{ border: '1px solid lightgray', padding: '10px' }}>
                        <Typography style={{ fontWeight: 'bold' }} mb={0.5}>Budget Comparison</Typography>
                        <Divider />
                        <Box mt={0.5}>
                            <DemoTypo>This budget is
                                {defaultPointTime?.name ? (<span
                                    style={{
                                        color: (actualAmount !== null ? result - actualAmount : (result - (totalTax + budget?.total_cost))) >= 0 ? 'green' : 'red',
                                        marginRight: 5,
                                        marginLeft: 5,
                                        fontWeight: 600
                                    }}>
                                    {`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(actualAmount !== null ? result - actualAmount : (result - (totalTax + budget?.total_cost)))}`}
                                </span>) : '--'}
                                {defaultPointTime?.name ? (actualAmount !== null ? result - actualAmount : (result - (totalTax + budget?.total_cost))) >= 0 ? 'under' : 'over' : ''} what was recorded on your default Point in Time.
                            </DemoTypo>
                            <Box mt={0.2}>
                                <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>{budgetType}</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <DemoTypo>Approved</DemoTypo>
                                    <DemoTypo1>{`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(approvedAmount !== null ? approvedAmount : projectData?.total_approved_budget)}`}</DemoTypo1>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <DemoTypo>Actual</DemoTypo>
                                    <DemoTypo1>{`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(actualAmount !== null ? actualAmount : (totalTax + budget?.total_cost))}`}</DemoTypo1>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <DemoTypo>Variance</DemoTypo>
                                    <DemoTypo1
                                        style={{
                                            color: budgetType === 'Unassigned' ? varianceAmount > 0 ? 'green' : 'red' : ((projectData?.total_approved_budget - (totalTax + budget?.total_cost)) >= 0 || varianceAmount > 0) ? 'green' : 'red'
                                        }}
                                    >{`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(varianceAmount !== null ? varianceAmount : (projectData?.total_approved_budget - (totalTax + budget?.total_cost)))}`}</DemoTypo1>
                                </Box>
                            </Box>
                            <Box mt={0.2}>
                                <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>Default Point In Time: <span style={{ fontWeight: 'normal', fontSize: 15 }}>{defaultPointTime?.name || '--'}</span></Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <DemoTypo>Actual At Point in Time</DemoTypo>
                                    <DemoTypo1>{`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(result || 0)}`}</DemoTypo1>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <DemoTypo>Variance</DemoTypo>
                                    <DemoTypo1
                                        style={{
                                            color: defaultPointTime?.name ? (actualAmount !== null ? result - actualAmount : (result - (totalTax + budget?.total_cost))) >= 0 ? 'green' : 'red' : 'lightgray'
                                        }}
                                    >{defaultPointTime?.name ? `${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(actualAmount !== null ? result - actualAmount : (result - (totalTax + budget?.total_cost)))}` : '--'}</DemoTypo1>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ border: '1px solid lightgray', padding: '10px' }} mt={0.5}>
                        <Typography style={{ fontWeight: 'bold' }} mb={0.5}>Budget Details</Typography>
                        <Divider />
                        <Box mt={0.5}>
                            {budgetType === 'Current Total Project' ? (
                                <Box mt={0.2}>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>{budgetType}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Budget Rooms</DemoTypo>
                                        <DemoTypo1>{budget?.room_count}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Budget Items</DemoTypo>
                                        <DemoTypo1>{budget?.item_count}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Average Tax</DemoTypo>
                                        <DemoTypo1>{`${Intl.NumberFormat('en-IN').format((tax * 100) / budget?.total_cost)} %`}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Tax Total</DemoTypo>
                                        <DemoTypo1>{`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(tax)}`}</DemoTypo1>
                                    </Box>
                                </Box>
                            ) : (
                                <Box mt={0.2}>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>{`${budgetType}`}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Budget ID</DemoTypo>
                                        <DemoTypo1>{individualBudget?.budget_no || '--'}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Budget Type</DemoTypo>
                                        <DemoTypo1>{BudgetType.find((a) => a.value === individualBudget?.type)?.name || '--'}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Expense Code</DemoTypo>
                                        <DemoTypo1>{individualBudget?.expanse_code || '--'}</DemoTypo1>
                                    </Box>
                                    {budgetType !== 'Unassigned' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <DemoTypo>Budget Rooms</DemoTypo>
                                            <DemoTypo1>{individualBudget?.item_assigned_budget_count || 0}</DemoTypo1>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Total Rooms with Budget Items</DemoTypo>
                                        <DemoTypo1>{individualBudget?.room_count}</DemoTypo1>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <DemoTypo>Budget Extended Items</DemoTypo>
                                        <DemoTypo1>{individualBudget?.ext_item_quantity}</DemoTypo1>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}

export default Overview;